/**
 * Horizontal-bar list of a single facet's value counts. The v8 generalisation
 * of CategoryBars (Phase 8 #9) — one component renders any dimension:
 * sentiment, general_tags, topic_tags, safety_tags.
 *
 * Read-only when `onSelect` is omitted; click-to-filter when provided (the
 * Overview cards read-only, the Comments dashboard + per-video card filter).
 *
 * Dark-terminal register (portal-wide design unification, 2026-06-12): this is
 * the YouTube analog of salience's TerminalBars — same track/label/mono-value
 * treatment so the two modules read as one instrument. `variant="sentiment"`
 * colours each bar by its key using the markets up/down hues (the one dimension
 * where colour carries meaning); every other facet uses the shared #4f8cff
 * accent, with `muted` keys (e.g. threat_or_abuse) dimmed.
 */

interface FacetBarsProps {
  counts: Record<string, number>;
  /** key → display label. Falls back to the key itself. */
  labelMap?: Record<string, string>;
  /** Current single-select filter value; null/undefined = none active. */
  selected?: string | null;
  /** Omit for a read-only chart; provide to make rows clickable filters. */
  onSelect?: (key: string) => void;
  /** Keys to drop entirely (noise, empty, retired). */
  hidden?: Iterable<string>;
  /** Keys rendered with a dimmed bar (e.g. threat_or_abuse). */
  muted?: Iterable<string>;
  /** Per-key sentiment colouring instead of the accent bar. */
  variant?: "default" | "sentiment";
  /** Show only the top-N rows (by count); the remainder collapse to a line. */
  maxRows?: number;
  emptyMessage?: string;
}

// Sentiment on dark panels uses the brighter markets up/down hues (the muted
// light-shell sentiment-* tokens don't read on bg-terminal).
const SENTIMENT_BAR: Record<string, string> = {
  supportive: "bg-up",
  unsupportive: "bg-down",
  neutral: "bg-terminal-ink-muted",
};

const ACCENT = "#4f8cff"; // shared with salience TerminalBars

export function FacetBars({
  counts,
  labelMap,
  selected,
  onSelect,
  hidden,
  muted,
  variant = "default",
  maxRows,
  emptyMessage = "Nothing tagged yet. This fills in as the AI works through the comments.",
}: FacetBarsProps) {
  const hiddenSet = new Set(hidden ?? []);
  const mutedSet = new Set(muted ?? []);

  const all = Object.entries(counts)
    .filter(([key]) => key !== "" && !hiddenSet.has(key))
    .sort((a, b) => b[1] - a[1]);

  // Percentages are over the *visible* total so a capped/filtered chart still
  // reads as a share of what's shown.
  const total = all.reduce((sum, [, n]) => sum + n, 0);
  const max = all[0]?.[1] ?? 0;
  const interactive = !!onSelect;

  const visible = maxRows != null ? all.slice(0, maxRows) : all;
  const hiddenCount = all.length - visible.length;
  const hiddenSum = all
    .slice(visible.length)
    .reduce((sum, [, n]) => sum + n, 0);

  if (all.length === 0) {
    return <div className="text-sm text-terminal-ink-muted">{emptyMessage}</div>;
  }

  const rowClasses =
    "grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_56px] items-center gap-2 text-sm sm:grid-cols-[minmax(0,180px)_1fr_72px_56px] sm:gap-3";

  return (
    <div className="space-y-2">
      {visible.map(([key, count]) => {
        const pct = total > 0 ? (100 * count) / total : 0;
        const widthPct = max > 0 ? (100 * count) / max : 0;
        const isMuted = mutedSet.has(key);
        const isSelected = selected === key;

        let barClass = "h-full rounded-full";
        let barStyle: React.CSSProperties = { width: `${widthPct}%` };
        if (isMuted) {
          barClass += " bg-white/15";
        } else if (variant === "sentiment") {
          barClass += ` ${SENTIMENT_BAR[key] ?? ""}`;
          if (!SENTIMENT_BAR[key]) barStyle = { ...barStyle, background: ACCENT };
        } else {
          barStyle = { ...barStyle, background: ACCENT };
          if (isSelected) barStyle.background = "#aab2ff";
        }

        const rowContent = (
          <>
            <div
              className={
                isMuted
                  ? "truncate text-terminal-ink-muted/60"
                  : "truncate text-terminal-ink-muted"
              }
              title={labelMap?.[key] ?? key}
            >
              {labelMap?.[key] ?? key}
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/8">
              <div className={barClass} style={barStyle} />
            </div>
            <div className="text-right font-mono text-xs tabular-nums text-terminal-ink">
              {count.toLocaleString("en-GB")}
            </div>
            {/* Percentage column hidden below sm to keep narrow rows readable. */}
            <div className="hidden text-right font-mono text-xs tabular-nums text-terminal-ink-muted sm:block">
              {pct.toFixed(1)}%
            </div>
          </>
        );

        if (!interactive) {
          return (
            <div key={key} className={rowClasses}>
              {rowContent}
            </div>
          );
        }

        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            aria-pressed={isSelected}
            className={`${rowClasses} -mx-2 cursor-pointer rounded px-2 py-1 text-left transition-colors hover:bg-white/5 ${
              isSelected ? "bg-white/8 ring-1 ring-white/25" : ""
            }`}
          >
            {rowContent}
          </button>
        );
      })}
      {hiddenCount > 0 && (
        <div className="px-2 text-right font-mono text-xs text-terminal-ink-muted">
          +{hiddenCount} more ({hiddenSum.toLocaleString("en-GB")})
        </div>
      )}
    </div>
  );
}

/** Display labels for the v8 general_tag dimension. */
export const GENERAL_TAG_LABELS: Record<string, string> = {
  question: "Question",
  challenge: "Challenge",
  misunderstanding: "Misunderstanding",
  personal_story: "Personal story",
  content_request: "Content request",
};

/** Display labels for the v8 sentiment dimension. */
export const SENTIMENT_LABELS: Record<string, string> = {
  supportive: "Supportive",
  unsupportive: "Unsupportive",
  neutral: "Neutral",
};

/** Display labels for the v8 safety_tag dimension. */
export const SAFETY_TAG_LABELS: Record<string, string> = {
  spam_bot: "Spam / bot",
  threat_or_abuse: "Threat / abuse",
};
